import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import fetcher, { FleetGetListParams, FleetPaginated } from '../fetcher';
import {
	AddressMasterData,
	BankMasterData,
	BouwheerMasterData,
	MasterData,
} from './master-data';
import { FileData, FilePayload } from './types';
import { SPFCovenantPayload } from './spf';
import {
	ProspectCustomerCreditCheck,
	SlikRobotikDetail,
	SlikRobotikResult,
	getProspectCreditCheck,
} from './prospect';

export type MPPSearchOption = {
	searchByOptions: MasterData[];
	frmOptions: MasterData[];
	fbdhOptions: MasterData[];
};

export const getMPPSearchOption = () => {
	return fetcher<MPPSearchOption>({
		api: '/mpp/get-search-options',
	});
};

export const useMPPSearchOptions = () => {
	return useQuery({
		queryKey: ['useMPPSearchOptions'],
		queryFn: async () => getMPPSearchOption(),
	});
};

export type MPPCustomerResult = {
	customerId: string;
	customerName: string;
	bmppGroupId: string;
	bmppGroupName: string;
	approverRoleCode: string;
	allowCreate: boolean;
	allowSlikRequest: boolean;
	allowApprove: boolean;
	allowCcaVerification: boolean;
	allowCcdhVerification: boolean;
	// CCA
	allowCreateSlikRequest: boolean;
	// CCDH
	allowApproveSlik: boolean;
	frmId: number;
	frmUsername: string;
	fbdhId: number;
	fbdhUsername: string;
	ccaId: number;
	ccaName: string;
	mppNo: string;
	mppId: number;
	mppStatus: string;
	applicationLetterFile: FileData;
	slikFormFile: FileData;
	mppSlikInquiryId?: number;
	slikStatus?: string;
};

export type SearchMPPCustomerParam = FleetGetListParams &
	Partial<{
		customerStatusId: number;
		applicationStatusId: number;
		frmId: number;
		fbdhId: number;
	}>;

const searchMPPCustomer = (body: SearchMPPCustomerParam) => {
	return fetcher<FleetPaginated<MPPCustomerResult>>({
		api: '/mpp/search-customer',
		body,
	});
};

export const useMPPCustomerSearch = (param: SearchMPPCustomerParam) => {
	return useQuery({
		queryKey: ['searchMPPCustomer', param] as const,
		queryFn: () => searchMPPCustomer(param),
	});
};

export type MPPCustomerDetailResult = {
	customerId: string;
	customerName: string;
	bmppGroupId: string;
	bmppGroupName: string;
	searchByOptions: MasterData[];
	plafonTypeOptions: MasterData[];
	financingLimitTypeOptions: MasterData[];
	orderSourceOptions: MasterData[];
	statusOptions: MasterData[];
};

export type GetMPPCustomerDetailParam = {
	id: string;
};

const getMPPCustomerDetail = (body: GetMPPCustomerDetailParam) => {
	return fetcher<MPPCustomerDetailResult>({
		api: '/mpp/get-customer-detail',
		body,
	});
};

export const useGetMPPCustomerDetail = (param: GetMPPCustomerDetailParam) => {
	return useQuery({
		queryKey: ['getMPPCustomerDetail', param],
		queryFn: () => getMPPCustomerDetail(param),
	});
};

export type MPPSPFResult = {
	spfId: number;
	napResolutionId: number;
	spfNo: string;
	mklNo: string;
	approvalDate: string;
	ntfAmount: number;
	outstandingPrincipal: number | null;
	existingFinancingLimit: number;
	financingLimitType: string;
	plafonType: string;
	expiredDate: string;
	orderSource: string;
	mklStatus: string;
	remainingLimit?: number | null;
	totalLimitAmount?: number | null;
};

export type SearchMPPSPFParam = FleetGetListParams &
	Partial<{
		approvalDate: string;
		financingLimitTypeId: number;
		plafonTypeId: number;
		expiredDate: string;
		orderSourceId: number;
		statusId: number;
		customerId: string;
	}>;

const searchMPPSPF = (body: SearchMPPSPFParam) => {
	return fetcher<FleetPaginated<MPPSPFResult>>({
		api: '/mpp/search-spf',
		body,
	});
};

export const useMPPSPFSearch = (param: SearchMPPSPFParam) => {
	return useQuery({
		queryKey: ['searchMPPSPF', param] as const,
		queryFn: () => searchMPPSPF(param),
	});
};

export type MPPResult = {
	id: number;
	customerId: string;
	customerName: string;
	bmppGroupName: string;
	mppStatus: MasterData;
	requestStatus: MasterData;
	applicationDate: string;
	frmName: string;
	fbdhName: string;
	ccaName: string;
	allowInput: boolean;
	allowView: boolean;
	allowDownload: boolean;
	allowUpload: boolean;
};

export type SearchMPPParam = FleetGetListParams;

const searchMPP = (body: SearchMPPParam) => {
	return fetcher<FleetPaginated<MPPResult>>({
		api: '/mpp/search-mpp',
		body,
	});
};

export const useMPPSearch = (param: SearchMPPParam) => {
	return useQuery({
		queryKey: ['searchMPP', param] as const,
		queryFn: () => searchMPP(param),
	});
};

export type MPPDetail = {
	customerId: string;
	slikInquiryId?: number;
	customerName: string;
	bmppGroupId: string;
	bmppGroupName: string;
	portfolioGuideline: string;
	industryClass: string;
	ultimateOwner: string;
	createdAt?: string;
	napId: number;
	napResolutionId: number;
	mklNo: string;
	spfId: number;
	spfNo: string;
	covenants: {
		id: number;
		covenant: string;
		category: MasterData;
		priority: MasterData;
		repeatFrequency: MasterData;
		repeatFrequencyCustom: {
			id: number;
			repeatValue: number;
			repeatFrequency: MasterData;
			onPattern: {
				dayOfMonth: number;
				month: MasterData;
			};
			onThePattern: {
				position: MasterData;
				dayOfWeek: MasterData;
				month: MasterData;
			};
		};
		monitoringType: MasterData;
		furtherAnalysis: MasterData;
		dueDate: string;
		uploadDocumentDate: string;
		overdueDays: number;
		notes: string;
		covenantDocument: FileData;
		status: string;
	}[];
	facilityNotification: {
		id: number;
		resolutionPlafonId: number;
		wayOfPaymentId: number;
		facilityValidityPeriod: string;
		disbursementMechanism: string;
		financingObjectCondition: string;
		financingObjectEvaluation: string;
		plafonTypeId: number;
		requestedLimit: number;
		approvedLimit: number;
		financingLimitTypeId: number;
		financingTypes: MasterData[];
		facilityTypes: MasterData[];
	}[];
	facilities: {
		id: number;
		assetType: MasterData;
		financingObjectCondition: MasterData;
		financingLimitType?: MasterData;
		plafonType: MasterData;
		tenor: MasterData;
		interestRateIndicationPercent: number;
		downPaymentPercent: number;
		securityDeposit: number;
		administrativeFee: number;
		provisionFee: number;
		serviceFee: number;
		fiduciaryFee: number;
		insurance: MasterData;
		insuranceDiscountPercentage: number | null;
	}[];
	backgroundRealisation: string;
	agingDate: string;
	customerFacility: {
		facilityType: string;
		pendingFacilities: {
			mppNo: string;
			ntf: number;
			outstanding: number;
			installment: number;
			unitCount: number;
			poId: number;
			poNo: string;
		}[];
		existingFacilities: {
			mppNo: string;
			ntf: number;
			outstanding: number;
			installment: number;
			unitCount: number;
			poId: number;
			poNo: string;
		}[];
		requestedFacilities: {
			mppNo: string;
			ntf: number;
			outstanding: number;
			installment: number;
			unitCount: number;
			poId: number;
			poNo: string;
		}[];
	};
	customerFacilitySummary: {
		totalPlafon: number;
		totalNtf: number;
		totalOutstanding: number;
		totalRemainingPlafon: number;
		totalUnitCount: number;
		totalCustomerApplication: number;
		installmentAmount: number;
	};
	customerAffiliatesFacility: {
		customerId: string;
		customerAffiliateName: string;
		customerAffiliateFacilities: {
			facilityType: string;
			pendingFacilities: {
				mppNo: string;
				ntf: number;
				outstanding: number;
				installment: number;
				unitCount: number;
				poId: number;
				poNo: string;
			}[];
			existingFacilities: {
				mppNo: string;
				ntf: number;
				outstanding: number;
				installment: number;
				unitCount: number;
				poId: number;
				poNo: string;
			}[];
		}[];
	}[];
	customerAffiliateFacilitySummary: {
		totalPlafon: number;
		totalNtf: number;
		totalOutstanding: number;
		totalRemainingPlafon: number;
		totalUnitCount: number;
		totalCustomerApplication: number;
		installmentAmount: number;
	};
	obligorDetail: {
		grandTotalPlafon: number;
		grandTotalApplication: number;
		grandTotalRemainingPlafon: number;
	};
	simulationOptions: {
		simulationId: number;
		spfId: number;
		facilityId: number;
		spfNo: string;
		mklNo: string;
		simulationNo: string;
		brandName: string;
		dealerName: string;
	}[];
	transactionDetailSummary: {
		assetType: string;
		productId: string;
		merk: string;
		unitCount: number;
		totalNtf: number;
		totalInstallment: number;
		totalDownPayment: number;
		creditSimulationId: number;
		creditSimulationNo: string;
		kkbRewardAvailableId: number;
		kkbRewardPercentage: number;
		kkbReferralName: string;
		bankId: number;
		bankAccountNumber: string;
		bankBranch: string;
		wayOfPaymentNotes: string;
		financialPurposeId: number;
		financingProduct: MasterData;
		insuranceType: string;
		insuranceAmount: number;
		spkFile: FileData;
		assetInformations: {
			assetId: number;
			bpkbOwnerName: string;
			assetUser: string;
			unitAmount: number;
			unitCount?: number;
			bouwheer: BouwheerMasterData;
			address: AddressMasterData;
		}[];
		isVerified: boolean;
		purchaseOrderId?: number;
		purchaseOrderNo: string;
		purchaseOrderStatus: string;
		hasDealerRefund: boolean;
		dealerRefundAmount: number;
		bank: BankMasterData;
		wayOfPayment: MasterData;
		financialPurpose: MasterData;
		segmentIndustry: string;
		allowFcohCheck?: boolean;
	}[];
	approvalHistory: {
		id: number;
		name: string;
		status: string;
		notes: string;
		process: string;
		date: string;
	}[];
	prospectId: string;
	napPlafonTypeId?: number;
	orderSource: MasterData;
	orderSources: MasterData[];
	approvalActionOptions?: MasterData[];
	financingPurpose?: MasterData[];
	resolutionFile?: FileData;
	categoryOptions: MasterData[];
	priorityOptions: MasterData[];
	customRepeatFrequencyOptions: MasterData[];
	repeatFrequencyOptions: MasterData[];
	monitoringType: MasterData[];
	furtherAnalysisOptions: MasterData[];
	positionOptions: MasterData[];
	monthOptions: MasterData[];
	dayOfWeekOptions: MasterData[];
	financingLimitTypeOptions: MasterData[];
	plafonTypeOptions: MasterData[];
	wayOfPaymentOptions: MasterData[];
	financialReports: {
		id: number;
		auditStatusId: number;
		auditorName: string;
		periodTypeId: number;
		periodTypeDetailId: number;
		year: number;
		financialReportFile: FileData;
		profitLoss: {
			id: number;
			operatingBusinessIncome: number;
			costOfRevenue: number;
			grossProfitLoss: number | null;
			otherIncome: number;
			otherExpense: number;
			profitLossBeforeTax: number | null;
			profitLossTheYear: number | null;
			ebitda: number | null;
			costOfMarketingAndAdmin: number;
			costOfDepreciationAndAmortization: number;
			businessProfit: number | null;
			taxBenefitOrCost: number;
		};
		balanceSheet: {
			id: number;
			cash: number;
			financingReceivableCurrentAsset: number;
			otherFinancialAssetInvestmentCurrentAsset: number;
			otherCurrentAsset: number;
			totalCurrentAsset: number | null;
			financingReceivableNonCurrentAsset: number;
			otherFinancialAssetInvestmentNonCurrentAsset: number;
			otherNonCurrentAsset: number;
			totalNonCurrentAsset: number | null;
			shortTermLoan: number;
			shortTermAccountPayable: number;
			otherShortTermLiability: number;
			totalShortTermLiability: number | null;
			longTermLoan: number;
			longTermAccountPayable: number;
			otherLongTermLiability: number;
			totalLongTermLiability: number | null;
			equity: number | null;
			totalAsset: number | null;
			totalLiability: number | null;
			totalLiabilityAndEquity: number | null;
		};
	}[];
	financialReportAnalysisResult: string;
	salesReports: {
		id: number;
		salesReport: FileData;
	};
	salesReportAnalysisResult: string;
	bankStatements: {
		id: number;
		bankStatementPeriod: string;
		bankStatementFile: FileData;
		accountType: number;
		accountNo: string;
		bankId: number;
		bank: MasterData;
		totalDebit: number;
		totalCredit: number;
		averageBalance: number;
	}[];
	bankStatementAnalysisResult: string;
	otherDocuments: {
		id: number;
		document: FileData;
		documentName: string;
		notes: string;
	}[];
};

export type GetMPPDetailParam =
	| {
			mppId: number;
	  }
	| {
			spfId: number;
	  };

const getMPPDetail = (body: GetMPPDetailParam) => {
	return fetcher<MPPDetail>({
		api: '/mpp/get-detail-mpp',
		body,
	});
};

export const useGetMPPDetail = (param: GetMPPDetailParam) => {
	return useQuery({
		queryKey: ['getMPPDetail', param],
		queryFn: () => getMPPDetail(param),
	});
};

export type SubmitMPPSlikRequestFilesPayload = {
	customerId?: string;
	slikFormFile: FilePayload;
	applicationLetterFile: FilePayload;
};

const submitMPPSlikRequestFiles = (body: SubmitMPPSlikRequestFilesPayload) => {
	return fetcher<void>({
		api: '/mpp/submit-slik-request-files',
		body,
	});
};

export const useSubmitMPPSlikRequestFilesMutation = () => {
	return useMutation({
		mutationFn: submitMPPSlikRequestFiles,
	});
};

export type GetMPPSlikRequestFilesParam = {
	spfId: number;
};

export type GetMPPSlikRequestFilesResponse = {
	slikInquiryId?: number;
	slikFormFile?: FileData;
	applicationLetterFile?: FileData;
};

export type MPPRequestSLIKResponse = {
	slikInquiryReffNumber: string;
	mppSlikInquiryId: number;
};

const requestMPPSlik = (customerId: string) => {
	return fetcher<MPPRequestSLIKResponse>({
		api: '/mpp/request-slik',
		body: {
			customerId,
		},
	});
};

export const useRequestMPPSlikMutation = () => {
	return useMutation({
		mutationFn: requestMPPSlik,
	});
};

const getMPPSlikRequest = (body: GetMPPSlikRequestFilesParam) => {
	return fetcher<GetMPPSlikRequestFilesResponse>({
		api: '/mpp/get-slik-request',
		body,
	});
};

export const useGetMPPSlikRequest = (param: GetMPPSlikRequestFilesParam) => {
	return useQuery({
		queryKey: ['getMPPSlikRequest', param],
		queryFn: () => getMPPSlikRequest(param),
	});
};

export type ManagementManualSlik = {
	id?: number;
	shareholderId?: number;
	name?: string;
	slikResult?: number;
	idebFile?: FileData;
};

export type ShareholderManualSlik = {
	id?: number;
	shareholderId?: number;
	name?: string;
	slikResult?: number;
	idebFile?: FileData;
};

export type GetManualSlikResponse = {
	slikFile?: FileData;
	slikResult?: number;
	managementManualSliks?: ManagementManualSlik[];
	shareholderManualSliks?: ShareholderManualSlik[];
};

const getManualMPPSlik = (body: { customerId: string }) => {
	return fetcher<GetManualSlikResponse>({
		api: '/mpp/get-manual-slik',
		body,
	});
};

export const useGetManualMPPSlik = (param: { customerId: string }) => {
	return useQuery({
		queryKey: ['getManualMPPSlik', param],
		queryFn: () => getManualMPPSlik(param),
	});
};

export type SubmitManualSlikManagement = {
	id?: number;
	shareholderId?: number;
	slikResult?: number;
	idebFile?: FilePayload;
};

export type SubmitManualSlikShareholder = {
	id?: number;
	shareholderId?: number;
	slikResult?: number;
	idebFile?: FilePayload;
};

export type SubmitManualSlikPayload = {
	id?: number;
	slikResult?: number;
	slikFile?: FilePayload;
	managementManualSliks?: SubmitManualSlikManagement[];
	shareholderManualSliks?: SubmitManualSlikShareholder[];
	notes?: string;
	approvalAction: number;
};

const submitManualMPPSlik = (body: SubmitManualSlikPayload) => {
	return fetcher<void>({
		api: '/mpp/submit-manual-slik',
		body,
	});
};

export const useSubmitManualMPPSlikMutation = () => {
	return useMutation({
		mutationFn: submitManualMPPSlik,
	});
};

export type SubmitMPPPayload = {
	id: number;
	spfId: number;
	covenants: SPFCovenantPayload[];
	transactionDetailSummary: SubmitMPPTransactionDetailSummary[];
	agingDate: string;
	notes: string;
	realizationBackground: string;
	mppCompletenessDataData: {
		mppId: number;
		bankStatements: SubmitMPPBankStatement[];
		financialReports: SubmitMPPFinancialReport[];
		salesReports: {
			id: number;
			salesReport: FileData;
		};
		otherDocuments: SubmitMPPOtherDocument[];
		salesReportAnalysisResult: string;
		financialReportAnalysisResult: string;
		bankStatementAnalysisResult: string;
	};
};

export type SubmitMPPOtherDocument = {
	id: number;
	document: FileData;
	documentName: string;
	notes: string;
};

export type SubmitMPPBankStatement = {
	id: number;
	bankStatementPeriod: string;
	bankStatementFile: FileData;
	accountType: number;
	accountNo: string;
	bankId: number | MasterData;
	totalDebit: number;
	totalCredit: number;
	averageBalance: number;
};

export type SubmitMPPFinancialReport = {
	id: number;
	auditStatusId: number;
	auditorName: string;
	periodTypeId: number;
	periodTypeDetailId: number;
	year: number;
	financialReportFile: FileData;
	profitLoss: {
		id: number;
		operatingBusinessIncome: number;
		costOfRevenue: number;
		grossProfitLoss: number;
		otherIncome: number;
		otherExpense: number;
		profitLossBeforeTax: number;
		profitLossTheYear: number;
		ebitda: number;
		costOfMarketingAndAdmin: number;
		costOfDepreciationAndAmortization: number;
		businessProfit: number;
		taxBenefitOrCost: number;
	};
	balanceSheet: {
		id: number;
		cash: number;
		financingReceivableCurrentAsset: number;
		otherFinancialAssetInvestmentCurrentAsset: number;
		otherCurrentAsset: number;
		totalCurrentAsset: number;
		financingReceivableNonCurrentAsset: number;
		otherFinancialAssetInvestmentNonCurrentAsset: number;
		otherNonCurrentAsset: number;
		totalNonCurrentAsset: number;
		shortTermLoan: number;
		shortTermAccountPayable: number;
		otherShortTermLiability: number;
		totalShortTermLiability: number;
		longTermLoan: number;
		longTermAccountPayable: number;
		otherLongTermLiability: number;
		totalLongTermLiability: number;
		equity: number;
		totalAsset: number;
		totalLiability: number;
		totalLiabilityAndEquity: number;
	};
};

export type SubmitMPPTransactionDetailSummary = {
	creditSimulationId: number;
	kkbRewardAvailableId: number;
	kkbRewardPercentage: number;
	kkbReferralName: string;
	bankId: number;
	bankAccountNumber: string;
	bankBranch: string;
	wayOfPaymentNotes: string;
	financialPurposeId: number;
	assetInformations: SubmitMPPAssetInformation[];
	spkFile: FilePayload;
};

export type SubmitMPPAssetInformation = {
	bpkbOwnerName: string;
	assetUser: string;
	bowheerId: string;
	unitAmount: number;
	postalCodeId: number;
};

const submitMPP = (body: SubmitMPPPayload) => {
	return fetcher<boolean>({
		api: '/mpp/submit',
		body,
	});
};
const submitMPPFCOM = (body: SubmitMPPPayload) => {
	return fetcher<boolean>({
		api: '/mpp/submit-mpp-fcom',
		body,
	});
};

export const useSubmitMPPMutation = () => {
	return useMutation({
		mutationFn: submitMPP,
	});
};

export const useSubmitMPPFCOMMutation = () => {
	return useMutation({
		mutationFn: submitMPPFCOM,
	});
};

export type ApproveMPPPayload = {
	mppId: number;
	action?: string;
	notes?: string;
};

const approveMPP = (body: ApproveMPPPayload) => {
	return fetcher<string>({
		api: '/mpp/submit-approval',
		body,
	});
};

export const useApproveMPPMutation = () => {
	const queryClient = useQueryClient();

	return useMutation({
		mutationFn: approveMPP,
		onSuccess: () => {
			// Invalidate queries to refresh approval flags
			queryClient.invalidateQueries({ queryKey: ['searchMPPCustomer'] });
			queryClient.invalidateQueries({ queryKey: ['searchMPP'] });
		},
		onError: (error) => {
			console.error('MPP approval failed:', error);
		},
	});
};

// Enhanced approval state management
export type ApprovalState = {
	currentLevel: 'FRM' | 'FBDH' | 'CCA' | 'CCDH';
	isFullyApproved: boolean;
	allowCreate: boolean;
	allowApprove: boolean;
	allowCcaVerification: boolean;
	allowCcdhVerification: boolean;
	allowCreateSlikRequest: boolean;
	allowApproveSlik: boolean;
};

// Function to determine approval flags based on current state
// Expected behavior for Create MPP button:
// - FRM can create MPP when: no existing MPP OR existing MPP is fully approved
// - FRM cannot create MPP when: MPP is pending approval OR MPP is in approval process
export const calculateApprovalFlags = (
	currentLevel: string,
	mppStatus: string,
	userRole: string,
): ApprovalState => {
	const isFullyApproved = mppStatus === 'FULLY_APPROVED';
	const isPending = mppStatus === 'PENDING';
	const isInApproval = mppStatus === 'IN_APPROVAL';

	// Debug logging for approval state calculation
	// console.log('Approval state calculation:', {
	// 	currentLevel,
	// 	mppStatus,
	// 	userRole,
	// 	isFullyApproved,
	// 	isPending,
	// 	isInApproval,
	// });

	// Base state
	const state: ApprovalState = {
		currentLevel: currentLevel as ApprovalState['currentLevel'],
		isFullyApproved,
		allowCreate: false,
		allowApprove: false,
		allowCcaVerification: false,
		allowCcdhVerification: false,
		allowCreateSlikRequest: false,
		allowApproveSlik: false,
	};

	// FRM can create new MPPs only when no MPP is in approval or when fully approved
	if (userRole === 'FRM') {
		// Allow creation when:
		// 1. No MPP exists (isPending = false, isInApproval = false, isFullyApproved = false)
		// 2. MPP is fully approved (can create new ones)
		// Disallow creation when MPP is in approval process
		state.allowCreate = isFullyApproved || (!isInApproval && !isPending);
	}

	// FBDH approval logic
	if (userRole === 'FBDH' && currentLevel === 'FBDH' && isPending) {
		state.allowApprove = true;
	}

	// CCA verification and approval logic
	if (userRole === 'CCA') {
		if (currentLevel === 'CCA' && isPending) {
			state.allowApprove = true;
			state.allowCcaVerification = true;
		}
		if (isFullyApproved) {
			state.allowCreateSlikRequest = true;
		}
	}

	// CCDH verification and approval logic
	if (userRole === 'CCDH') {
		if (currentLevel === 'CCDH' && isPending) {
			state.allowApprove = true;
			state.allowCcdhVerification = true;
		}
		if (currentLevel === 'CCDH' && isFullyApproved) {
			state.allowApproveSlik = true;
		}
	}

	// Debug logging for final approval state
	// console.log('Final approval state:', state);

	return state;
};

// Custom hook to use enhanced approval logic
export const useApprovalState = (
	record: MPPCustomerResult,
	userRole: string,
) => {
	return calculateApprovalFlags(
		record.approverRoleCode || '',
		record.mppStatus || '',
		userRole,
	);
};

export type ApproveMPPSLIKPayload = {
	mppSlikInquiryId: number;
	action: string;
	notes: string;
	selectedCompanyIndexes?: number[];
	selectedShareholderIndexes?: {
		referenceNumber: string;
		secondIndexes: number[];
	}[];
};

const approveMPPSLIK = (body: ApproveMPPSLIKPayload) => {
	return fetcher<string>({
		api: '/mpp/submit-approval-slik',
		body,
	});
};

export const useApproveMPPSLIKMutation = () => {
	return useMutation({
		mutationFn: approveMPPSLIK,
	});
};

export type GetMPPHistoryPayload = FleetGetListParams & {
	customerId: string;
	mppDate?: string;
	mppStatusId?: string;
	applicationId?: string;
	agreementNo?: string;
	supplierName?: string;
};

export const getMPPHistory = (body: GetMPPHistoryPayload) => {
	return fetcher<
		{
			mklNo: string;
			mppId: number;
			mppNo: string;
			mppDate: string;
			ntf: number;
			mppStatus: string;
			approval: string;
			applicationId: string;
			agreementNo: string;
			supplierName: string;
			mppFile: FileData;
		}[]
	>({
		api: '/mpp/get-mpp-history',
		body,
	});
};

export const useMPPHistory = (param: GetMPPHistoryPayload) => {
	return useQuery({
		queryKey: ['getMPPHistory', param],
		queryFn: () => getMPPHistory(param),
	});
};

export const getMPPHistoryOptions = () => {
	return fetcher<{
		searchByOptions: MasterData[];
		mppStatusOptions: MasterData[];
	}>({
		api: '/mpp/get-history-search-options',
	});
};

export const useMPPHistoryOptions = () => {
	return useQuery({
		queryKey: ['getMPPHistoryOptions'],
		queryFn: () => getMPPHistoryOptions(),
	});
};

export const getMPPSLIKHistory = (customerId: string) => {
	return fetcher<{
		history: {
			id: 0;
			referenceNumber: 'string';
			createdAt: '2025-07-22T08:31:02.620Z';
			lastModifiedAt: '2025-07-22T08:31:02.620Z';
		}[];
	}>({
		api: '/mpp/get-slik-history',
		body: {
			customerId,
		},
	});
};

export const useMPPSLIKHistory = (customerId: string) => {
	return useQuery({
		queryKey: ['getMPPSLIKHistory', customerId],
		queryFn: () => getMPPSLIKHistory(customerId),
	});
};

export type MPPManualSLIKResult = {
	slikFile: FileData;
	slikResult: number;
	managementManualSliks: {
		id: number;
		shareholderId: number;
		name: string;
		slikResult: number;
		identityNumber: string;
		idebFile: FileData;
	}[];
	shareholderManualSliks: {
		id: number;
		shareholderId: number;
		name: string;
		slikResult: number;
		identityNumber: string;
		idebFile: FileData;
	}[];
};

export type MPPManualSLIKMainInfo = {
	customerId: string;
	customerName: string;
	customerGroup: string;
	customerGroupId: string;
	industrySector: string;
	portofolioGuideline: string;
	prospectId: string;
	npwp: string;
};

export type MPPSSlikDetailResponse = {
	isManualSlik: boolean;
	manualSlikResult?: MPPManualSLIKResult;
	roboticSlikResult: SlikRobotikDetail;
	roboticSlikRequestResult: SlikRobotikResult;
	customerMainInformation: MPPManualSLIKMainInfo;
	ccaNotes: string;
	isEnableSlikRobotic: boolean;
};

export const getMPPSLIKResult = (mppSlikInquiryId: number) => {
	return fetcher<MPPSSlikDetailResponse>({
		api: '/mpp/get-slik-result',
		body: {
			mppSlikInquiryId,
		},
	});
};

export const useMPPSLIKResult = (mppSlikInquiryId: number | undefined) => {
	return useQuery({
		queryKey: ['getMPPSLIKResult', mppSlikInquiryId],
		queryFn: async () => {
			if (!mppSlikInquiryId || mppSlikInquiryId <= 0) {
				throw new Error('Invalid SLIK inquiry ID');
			}

			const result = await getMPPSLIKResult(mppSlikInquiryId);
			const prospectId = result.data.roboticSlikRequestResult.details.find(
				(detail) => detail.prospectId,
			)?.prospectId;

			let prospectDetail: null | ProspectCustomerCreditCheck = null;
			if (prospectId && !result.data?.isManualSlik) {
				const prospectRes = await getProspectCreditCheck(prospectId);
				prospectDetail = prospectRes.data;
			}

			return [result.data, prospectDetail] as const;
		},
		enabled: !!mppSlikInquiryId && mppSlikInquiryId > 0,
	});
};

export const getMPPThingsTodo = () => {
	return fetcher<number>({
		api: '/mpp/get-things-todo',
	});
};

export const useMPPThingsTodo = () => {
	return useQuery({
		queryKey: ['getMPPThingsTodo'],
		queryFn: async () => getMPPThingsTodo(),
	});
};

export type GetSimulationListParam = FleetGetListParams & {
	spfId?: number;
	dealerName?: string;
};

export type GetSimulationListResponse = FleetPaginated<{
	simulationId: number;
	spfId: number;
	facilityId: number;
	spfNo: string;
	mklNo: string;
	simulationNo: string;
	brandName: string;
	dealerName: string;
}>;

export const getSimulationList = (body: GetSimulationListParam) => {
	return fetcher<GetSimulationListResponse>({
		api: '/mpp/get-simulation-list',
		body,
	});
};

export const useSimulationList = (param: GetSimulationListParam) => {
	return useQuery({
		queryKey: ['getSimulationList', param],
		queryFn: () => (param.spfId ? getSimulationList(param) : null),
	});
};

export const getSimulationDetail = (creditSimulationId: number) => {
	return fetcher<{
		assetType: string;
		financingObject: string;
		brand: string;
		unitCount: number;
		totalNtf: number;
		totalInstallment: number;
		totalDownPayment: number;
		insuranceAmount: number;
		insuranceType: string;
		productId: string;
		productName: string;
		segmentIndustry: string;
		financingProduct: MasterData;
	}>({
		api: '/mpp/select-credit-simulation',
		body: {
			creditSimulationId,
		},
	});
};

export const useGetSimulationDetail = () => {
	return useMutation({
		mutationFn: getSimulationDetail,
	});
};

export const downloadMPP = (params: { mppId: number; spfId?: number }) => {
	return fetcher<{
		signedUrl: string;
	}>({
		api: '/mpp/download',
		body: {
			mppId: params.mppId,
			...(params.spfId !== undefined && { spfId: params.spfId }),
		},
	});
};

export const useDownloadMPP = () => {
	return useMutation({
		mutationFn: downloadMPP,
	});
};

// 6-Month Rule Validation Logic
const MPP_TIME_THRESHOLD = 180; // 6 months in days

export type TimeValidation = {
	daysSinceSpfApproval: number;
	requiresExtendedProcess: boolean;
	additionalDocumentsRequired: string[];
	isWithinThreshold: boolean;
};

// Calculate days between two dates
const daysBetween = (startDate: Date, endDate: Date): number => {
	const timeDiff = Math.abs(endDate.getTime() - startDate.getTime());
	return Math.ceil(timeDiff / (1000 * 3600 * 24));
};

export const validateMPPTiming = (
	spfApprovalDate: Date,
	referenceDate?: Date,
): TimeValidation => {
	const comparisonDate = referenceDate || new Date();
	const daysSince = daysBetween(spfApprovalDate, comparisonDate);
	const requiresExtended = daysSince > MPP_TIME_THRESHOLD;

	return {
		daysSinceSpfApproval: daysSince,
		requiresExtendedProcess: requiresExtended,
		isWithinThreshold: !requiresExtended,
		additionalDocumentsRequired: requiresExtended
			? [
					'financial_reports',
					'bank_statements',
					'sales_reports',
					'delay_justification',
					'updated_credit_analysis',
				]
			: [],
	};
};

// Validation rules for MPP form inputs
export const MPP_VALIDATION_RULES = {
	downPayment: {
		rule: 'inputDownPayment >= spfApprovedDownPayment',
		message: 'Down payment tidak boleh lebih rendah dari SPF yang disetujui',
		validate: (inputValue: number, spfValue: number) => inputValue >= spfValue,
	},
	effectiveRate: {
		rule: 'inputRate >= rkpMinimumRate',
		message:
			'Effective rate tidak boleh lebih rendah dari minimum RKP (misal: 9%)',
		validate: (inputValue: number, minimumValue: number) =>
			inputValue >= minimumValue,
	},
	tenor: {
		rule: 'tenor <= 7',
		message: 'Tenor maksimum 7 tahun',
		validate: (inputValue: number) => inputValue <= 7,
	},
	unitCount: {
		rule: 'inputValue > 0',
		message: 'Jumlah unit harus lebih besar dari 0',
		validate: (inputValue: number) => inputValue > 0,
	},
	servicingFee: {
		rule: 'servicingFee = provisionFee * 0.75',
		message: 'Servicing fee otomatis dihitung sebagai 75% dari provision fee',
		calculate: (provisionFee: number) => provisionFee * 0.75,
	},
};

// Split PO Configuration Logic
export type SplitPOConfiguration = {
	isSplitPO: boolean;
	assetType: string;
	supportsMultiplePO: boolean;
	poConfiguration: {
		chassisPO?: {
			supplier: string;
			amount: number;
		};
		karoseriPO?: {
			supplier: string;
			amount: number;
		};
	};
};

export const determineSplitPOConfig = (
	assetType: string,
	isSplitPO: boolean,
	chassisAmount?: number,
	karoseriAmount?: number,
): SplitPOConfiguration => {
	const supportsMultiplePO = assetType === 'COMMERCIAL' && isSplitPO;

	return {
		isSplitPO,
		assetType,
		supportsMultiplePO,
		poConfiguration: supportsMultiplePO
			? {
					chassisPO: chassisAmount
						? {
								supplier: 'Chassis Supplier',
								amount: chassisAmount,
							}
						: undefined,
					karoseriPO: karoseriAmount
						? {
								supplier: 'Karoseri Supplier',
								amount: karoseriAmount,
							}
						: undefined,
				}
			: {},
	};
};
